import { FC } from 'react';
import { matchPath, useLocation, useRouteMatch } from 'react-router-dom';
import { Role } from 'src/apis/monolith/types';
import { GroupWrap } from 'src/components/group-wrap';
import { ActiveSection } from 'src/components/layout/header/types';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useDirectFcfsFlags } from 'src/hooks/use-direct-fcfs-flags';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';

const routes = [
  '/start',
  '/activity',
  '/notifications',
  '/uploads',
  '/shippers', // special case: see below
  '/platform-settings',
  '/maintenance-messages',
  '/operators',
  '/shipper',
  '/dashboard', // special case: see below
  '/snam-bid-collector',
  '/direct-fcfs',
];
const angularRoutes = [
  '#/start',
  '#/notifications',
  '#/uploads',
  '#/settings',
  '#/maintenance-messages',
  '#/activity',
  '#/platform-settings',
];

export const isDashboardActive = (
  pathname: string,
  hash: string,
  role?: Role
) =>
  routes.some((route) =>
    // for SSOs some paths live in the storage section
    role === 'SSO_ADMIN' &&
    (matchPath(pathname, { path: '/shippers' }) ||
      matchPath(pathname, { path: '/dashboard' }) ||
      matchPath(pathname, { path: '/settings' }))
      ? false
      : Boolean(matchPath(pathname, { path: route }))
  ) ||
  (pathname.startsWith('/platform') &&
    angularRoutes.some((angularRoute) => hash.startsWith(angularRoute)));

type SecondaryNavDashboardProps = {
  isSnamAdmin: boolean;
  activeSection: ActiveSection;
};

export const SecondaryNavDashboard: FC<SecondaryNavDashboardProps> = (
  props
) => {
  const { isSnamAdmin, activeSection } = props;
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const { minTablet } = useBreakpoints();
  const { pathname } = useLocation();

  const directFcfsFlags = useDirectFcfsFlags();
  const isDirectFcfsSection = useRouteMatch('/direct-fcfs');
  const renderDirectFcfsLink =
    directFcfsFlags.data?.roles.includes('TSO_USER') || isDirectFcfsSection;

  if (!activeSection.dashboard) return null;

  return (
    <GroupWrap
      gap={2}
      data-testid="secondary-nav"
      alignItems={!minTablet ? 'stretch' : 'center'}
      flexDirection={!minTablet ? 'column' : 'row'}
    >
      {(monolithUser?.isShipper || monolithUser?.isTso) && (
        <ExternalLink
          mode="secondary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/start`}
        >
          Dashboard
        </ExternalLink>
      )}
      {monolithUser?.isSso && (
        <ExternalLink
          mode="secondary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/storage/offers`}
        >
          Manage Offer
        </ExternalLink>
      )}
      {monolithUser?.isSso && (
        <ExternalLink
          mode="secondary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/storage/offers/create`}
        >
          Create Offer
        </ExternalLink>
      )}
      {monolithUser?.isShipper && (
        <ExternalLink
          mode="secondary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/activity`}
        >
          Activity
        </ExternalLink>
      )}

      {monolithUser?.isShipper && (
        <Link
          mode="secondary"
          to="/operators"
          isActive={
            pathname.startsWith('/operators') ||
            pathname.startsWith('/dashboard/operators')
          }
        >
          Operators
        </Link>
      )}

      {monolithUser?.isTso && (
        <ExternalLink
          mode="secondary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/notifications`}
        >
          Notifications
        </ExternalLink>
      )}
      {monolithUser?.isTso && (
        <ExternalLink
          mode="secondary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/uploads`}
        >
          Uploads
        </ExternalLink>
      )}

      {renderDirectFcfsLink && (
        <Link mode="secondary" to="/direct-fcfs">
          Direct FCFS
        </Link>
      )}

      {monolithUser?.isTso && (
        <Link
          mode="secondary"
          to="/shippers"
          isActive={
            pathname.startsWith('/shippers') ||
            pathname.startsWith('/dashboard/shippers')
          }
        >
          Shippers
        </Link>
      )}
      {monolithUser?.isTso && (
        <ExternalLink
          mode="secondary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/maintenance-messages`}
        >
          Maintenance Messages
        </ExternalLink>
      )}
      {isSnamAdmin && (
        <Link mode="secondary" to="/snam-bid-collector">
          Bid Collector
        </Link>
      )}
    </GroupWrap>
  );
};
